// DeliveryReport — the Reports tab's delivery performance view: how much of
// what fell due was delivered on time, which months carried the slippage, and
// which items are still outstanding.
//
// The view holds no derivation. Every figure, including each month's on-time
// rate, arrives from the server, which withholds a rate whose sample cannot
// support it. Computing one here would contradict that rule the moment a
// filter narrowed a month to one or two deliveries.
//
// The record-type filter is a server filter. It is applied to the item list
// before any derivation runs, so switching it refetches; narrowing an
// all-records payload on the client would leave the metrics, the chart and
// the tables describing three different sets of records.
//
// Failure is whole-view by design, unlike the summary's per-section fail-open:
// one data domain backs this report, so a failed read leaves nothing to
// render honestly, and a zeroed chart would read as a contract with no
// delivery history. The filter chips stay put through the loading, error and
// empty states, so a combination that produced nothing is always recoverable.

import { useSearchParams } from "react-router";
import { Alert, LoadingSpinner } from "../../common";
import MetricCard from "../tracking/deliverable/MetricCard";
import SlipTag from "../tracking/milestone/SlipTag";
import DeliverableSlipTag from "../tracking/deliverable/DeliverableSlipTag";
import ReportSection from "./ReportSection";
import RecordTypeChips from "./RecordTypeChips";
import { TD_CLASS, TD_NUMERIC_CLASS, Th } from "./reportTable";
import MonthlyStackedColumns from "./charts/MonthlyStackedColumns";
import { notYetDueCount } from "./charts/deliveryTooltip";
import {
  countTone,
  formatPercent,
  formatRateTrend,
  formatReportMonth,
  onTimeRateTone,
} from "./reportFormat";
import { nextReportParams } from "./reportUrlState";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import { useReportDelivery } from "../../../hooks/useContractReport";
import {
  DELIVERY_RECORD_TYPES,
  type DeliveryBucket,
  type DeliveryLateItem,
  type DeliveryRecordType,
  type DeliveryReportModel,
  type ReportRange,
} from "../../../types/report";

const DEFAULT_RECORD_TYPE: DeliveryRecordType = "all";

const RECORD_TYPE_LABEL: Record<DeliveryLateItem["recordType"], string> = {
  milestone: "Milestone",
  deliverable: "Deliverable",
};

function isRecordType(value: string | null): value is DeliveryRecordType {
  return !!value && (DELIVERY_RECORD_TYPES as readonly string[]).includes(value);
}

export interface DeliveryReportProps {
  contractId: string;
  /** Owned by the Reports tab, which renders the range chips for every
   *  focused view. */
  range: ReportRange;
}

export default function DeliveryReport({
  contractId,
  range,
}: DeliveryReportProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // An unrecognised record type falls back for rendering but is left in the
  // address bar, the same rule the view and range params follow.
  const recordTypeParam = searchParams.get("recordType");
  const recordType: DeliveryRecordType = isRecordType(recordTypeParam)
    ? recordTypeParam
    : DEFAULT_RECORD_TYPE;

  const { data, isPending, error } = useReportDelivery(
    contractId,
    range,
    recordType,
  );

  function chooseRecordType(next: DeliveryRecordType) {
    setSearchParams(
      nextReportParams(searchParams, "recordType", next, DEFAULT_RECORD_TYPE),
      { replace: true },
    );
  }

  return (
    <div className="space-y-4">
      <RecordTypeChips value={recordType} onChange={chooseRecordType} />
      {isPending ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="md" />
        </div>
      ) : error || !data ? (
        <Alert variant="error">
          The delivery performance report couldn&apos;t be loaded. Refresh to
          try again.
        </Alert>
      ) : (
        <DeliveryBody model={data} range={range} />
      )}
    </div>
  );
}

function DeliveryBody({
  model,
  range,
}: {
  model: DeliveryReportModel;
  range: ReportRange;
}) {
  const { metrics, buckets, lateItems } = model;
  const trend = formatRateTrend(
    metrics.onTimeRate,
    metrics.prevRangeOnTimeRate,
    range,
  );
  const nothingDue = buckets.every((bucket) => bucket.due === 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <MetricCard
            testId="delivery-on-time"
            label="Delivered on time"
            value={formatPercent(metrics.onTimeRate)}
            valueTone={onTimeRateTone(metrics.onTimeRate)}
            // Quoting the counts beside a withheld rate reads as a
            // contradiction: the numbers are right there, so why is there no
            // percentage? Name the reason instead.
            subtitle={
              metrics.onTimeRate === null
                ? `Not enough data yet (${metrics.totalResolved} delivered)`
                : `${metrics.onTimeCount} of ${metrics.totalResolved} delivered`
            }
          />
          <MetricCard
            testId="delivery-late"
            label="Delivered late"
            value={metrics.lateCount}
            valueTone={countTone(metrics.lateCount, "warning")}
            subtitle={
              metrics.medianSlipDays === null
                ? undefined
                : `Median slip ${metrics.medianSlipDays} days`
            }
          />
          <MetricCard
            testId="delivery-still-overdue"
            // Scoped to items DUE in the window, so it must not read as the
            // contract's whole outstanding backlog.
            label="Still overdue in range"
            value={metrics.stillOverdueCount}
            valueTone={countTone(metrics.stillOverdueCount, "danger")}
            subtitle={
              metrics.oldestOverdueDays === null
                ? undefined
                : `Oldest ${metrics.oldestOverdueDays} days past due`
            }
          />
        </div>
        {trend !== null && (
          <p
            data-testid="delivery-trend"
            className="mt-2 text-caption text-slate-500 dark:text-slate-400"
          >
            On-time delivery: {trend}
          </p>
        )}
      </div>

      {nothingDue ? (
        <ReportSection id="delivery-months" title="Delivery by due month">
          <p
            data-testid="delivery-empty"
            className="text-body-sm text-slate-500 dark:text-slate-400"
          >
            Nothing was due in this window. Widen the reporting range, or
            change the record type, to see earlier delivery history.
          </p>
        </ReportSection>
      ) : (
        <>
          <ReportSection id="delivery-chart" title="Delivery by due month">
            <MonthlyStackedColumns buckets={buckets} />
            <PendingThisMonth buckets={buckets} />
          </ReportSection>

          <ReportSection id="delivery-months" title="Monthly breakdown">
            <MonthlyBreakdownTable buckets={buckets} />
          </ReportSection>

          <ReportSection id="delivery-late-items" title="Late and overdue items">
            {lateItems.length === 0 ? (
              <p className="text-body-sm text-slate-500 dark:text-slate-400">
                No late or overdue items in this window.
              </p>
            ) : (
              <LateItemsTable items={lateItems} />
            )}
          </ReportSection>
        </>
      )}
    </div>
  );
}

/**
 * Accounts for the gap between the last column's height and its due total.
 *
 * Items still inside their due date carry no outcome, so they appear in the
 * month's `due` figure and in none of the three segments. Only the final
 * month can hold any — an earlier month's undelivered item is overdue by
 * definition — and without this line the reader is left to wonder why the
 * column stops short of the number beside it.
 */
function PendingThisMonth({ buckets }: { buckets: DeliveryBucket[] }) {
  const current = buckets[buckets.length - 1];
  const pending = notYetDueCount(current);
  if (pending === 0) return null;
  const month = formatReportMonth(current.month);
  return (
    <p
      data-testid="delivery-not-yet-due"
      className="mt-2 text-caption text-slate-500 dark:text-slate-400"
    >
      {pending === 1
        ? `1 item due in ${month} has not reached its due date, so it has no outcome yet.`
        : `${pending} items due in ${month} have not reached their due date, so they have no outcome yet.`}
    </p>
  );
}

function MonthlyBreakdownTable({ buckets }: { buckets: DeliveryBucket[] }) {
  return (
    <div
      className="overflow-x-auto"
      tabIndex={0}
      role="group"
      aria-label="Monthly delivery breakdown"
    >
      <table className="w-full">
        <caption className="sr-only">
          Delivery outcomes per due month, with the month&apos;s on-time rate
          where its sample supports one
        </caption>
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
            <Th>Month</Th>
            <Th numeric>Due</Th>
            <Th numeric>On time</Th>
            <Th numeric>Late</Th>
            <Th numeric>Still overdue</Th>
            <Th numeric>On-time rate</Th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket) => (
            <tr
              key={bucket.month}
              data-testid={`delivery-month-${bucket.month}`}
              className="border-b border-slate-100 dark:border-slate-700"
            >
              <td className={TD_CLASS}>{formatReportMonth(bucket.month)}</td>
              <td
                data-testid={`delivery-month-${bucket.month}-due`}
                className={TD_NUMERIC_CLASS}
              >
                {bucket.due}
              </td>
              <td className={TD_NUMERIC_CLASS}>{bucket.onTime}</td>
              <td className={TD_NUMERIC_CLASS}>{bucket.late}</td>
              <td className={TD_NUMERIC_CLASS}>{bucket.stillOverdue}</td>
              <td
                data-testid={`delivery-month-${bucket.month}-rate`}
                className={TD_NUMERIC_CLASS}
              >
                {formatPercent(bucket.onTimeRate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LateItemsTable({ items }: { items: DeliveryLateItem[] }) {
  return (
    <div
      className="overflow-x-auto"
      tabIndex={0}
      role="group"
      aria-label="Late and overdue items"
    >
      <table className="w-full">
        <caption className="sr-only">
          Items in this window delivered after their due date or still
          outstanding, most recently due first
        </caption>
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
            <Th>Item</Th>
            <Th>Type</Th>
            <Th>Due</Th>
            <Th>Delivered</Th>
            <Th>Slip</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              data-testid={`delivery-late-item-${item.id}`}
              className="border-b border-slate-100 dark:border-slate-700"
            >
              <td className={TD_CLASS}>{item.name}</td>
              <td className="px-3 py-2 text-body-sm text-slate-600 dark:text-slate-300">
                {RECORD_TYPE_LABEL[item.recordType]}
              </td>
              <td className="px-3 py-2 text-body-sm tabular-nums text-slate-900 dark:text-slate-100">
                {formatDayMonthYear(item.dueDate)}
              </td>
              <td
                data-testid={`delivery-late-item-${item.id}-delivered`}
                className="px-3 py-2 text-body-sm tabular-nums text-slate-900 dark:text-slate-100"
              >
                {item.deliveredDate === null ? (
                  <NotDelivered />
                ) : (
                  formatDayMonthYear(item.deliveredDate)
                )}
              </td>
              <td className="px-3 py-2">
                <ItemSlipTag item={item} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** An undelivered item's empty date cell. A dash carries nothing when spoken,
 *  so the state is named for a screen reader and the glyph is hidden from it. */
function NotDelivered() {
  return (
    <>
      <span aria-hidden="true">—</span>
      <span className="sr-only">Not delivered</span>
    </>
  );
}

/** Each tab owns its own slip vocabulary — a milestone is "delivered" late, a
 *  deliverable is "submitted" late — so the row reuses the pill of the tab the
 *  item belongs to rather than inventing a third wording for the report. */
function ItemSlipTag({ item }: { item: DeliveryLateItem }) {
  if (item.recordType === "milestone") {
    return (
      <SlipTag
        variant={item.outcome === "stillOverdue" ? "late" : "deliveredLate"}
        days={item.slipDays}
      />
    );
  }
  return (
    <DeliverableSlipTag
      variant={item.outcome === "stillOverdue" ? "late" : "submitted_late"}
      days={item.slipDays}
      dueDate={item.dueDate}
      submittedAt={item.deliveredDate}
    />
  );
}
